import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Bugsnag from "@bugsnag/js";
import axios from "axios";
import cookie from "react-cookies";
import constants from "../../Constants";
import util from "../../util";
import AddressFields from "./AddressFields";
import VehiclePicker from "./VehiclePicker";
import VehicleDetail from "./VehicleDetail";
import AISuggestions from "./AISuggestions";
import QuoteResult from "./QuoteResult";
import BookingForm from "./BookingForm";
import AutoResizeTextarea from "./AutoResizeTextarea";
import ScrollNudge from "./ScrollNudge";
import { useScrollNudge } from "./useScrollNudge";
import { normaliseDashes, getOrCreateQuoteSessionId, buildQuoteSource_, trackAiSuggestionEvent, isWidgetEmbed, getWidgetSiteId, notifyWidgetParent } from "./helpers";
import { findVehicle } from "./helpers";
import { persistOrderTrackSession } from "../../util/orderChatSession";
import {
  extrasCartSignature,
  extrasLinesSignature,
} from "./tripExtrasHelpers";
import { resolveQuoteExtrasStoreEnabled } from "./quoteExtrasConfig";

const baseURL = constants.baseURL;

function extrasCartIsDirty(cart, extrasLines) {
  return extrasCartSignature(cart) !== extrasLinesSignature(extrasLines);
}

const EMPTY_QUOTE = {
  tripDistance: 0,
  tripDuration: 0,
  sellingPrice: 0,
  storedQuoteId: "",
  discount: "",
  otherOptions: [],
  quotedRunners: 0,
  quotedFloors: 0,
  quotedPromocode: "",
  quotedCollectiontime: "",
  quoteLatencyMs: null,
  extrasLines: [],
  extrasTotalCents: 0,
  extrasSubtotalCents: 0,
  extrasVatCents: 0,
};

function buildExtrasPayload(cart) {
  return Object.entries(cart || {})
    .map(([productId, qty]) => ({
      productId,
      qty: parseInt(qty, 10) || 0,
    }))
    .filter((line) => line.qty > 0);
}

// ─── Persistence (localStorage) ───────────────────────────────────────────
//
// On reload we restore the customer's in-progress quote so they don't have to
// re-enter the trip. PII is deliberately excluded - emails, phone numbers and
// names are never written to localStorage, so a shared device doesn't leak.
//
//   - URL params still win over saved state (explicit share-links work).
//   - Saved state is wiped on successful order placement.
//   - "v2" suffix lets us safely change the schema later.

const LS_KEY = "qt.lp2.quote.v2";
const WIDGET_LS_KEY = "qt.widget.quote.v1";

// Allow-list of non-PII fields we mirror to localStorage.
const PERSIST_FIELDS = [
  "truckType",
  "collection",
  "delivery",
  "usercategory",
  "promocode",
  "runners",
  "floors",
  "description",
  "collectiondate",
  "collectiontime",
  "showMore",
  "agreeToTerms",
  "extrasCart",
];

function loadSaved(storageKey = LS_KEY) {
  try {
    const raw = window.localStorage?.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function clearSaved(storageKey = LS_KEY) {
  try {
    window.localStorage?.removeItem(storageKey);
  } catch (_) {
    /* noop */
  }
}

// Strip quote-prefill params from the URL so a refresh after "Start over"
// does not resurrect addresses / vehicle / description from the link.
function stripQuoteUrlParams() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const keys = [
      "promocode",
      "trucktype",
      "description",
      "collectiondate",
      "collectiontime",
      "category",
      "prospectemail",
      "prospectmobilenumber",
      "v",
      "runners",
      "floors",
      "collectionlat",
      "collectionlng",
      "deliverylat",
      "deliverylng",
    ];
    let changed = false;
    for (const k of keys) {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  } catch (_) {
    /* noop */
  }
}

function saveSnapshot(state, storageKey = LS_KEY) {
  try {
    const snap = {};
    for (const key of PERSIST_FIELDS) {
      const v = state[key];
      // Skip the empty defaults so we don't bloat the saved blob.
      if (v === undefined || v === null || v === "" || v === false || v === 0) {
        continue;
      }
      if (
        typeof v === "object" &&
        !Array.isArray(v) &&
        Object.keys(v).every((k) => !v[k])
      ) {
        continue;
      }
      snap[key] = v;
    }
    if (Object.keys(snap).length === 0) {
      window.localStorage?.removeItem(storageKey);
      return;
    }
    window.localStorage?.setItem(storageKey, JSON.stringify(snap));
  } catch (_) {
    /* localStorage may be full / disabled - silently ignore */
  }
}

/**
 * Returns only the URL params that were ACTUALLY set, so we can later merge
 * `{ ...defaults, ...saved, ...urlParams }` and have explicit links win
 * without nuking saved state with empty strings.
 */
function readUrlParams() {
  const out = {};
  try {
    const q = new URLSearchParams(window.location.search);
    const stringMap = {
      promocode: "promocode",
      truckType: "trucktype",
      description: "description",
      collectiondate: "collectiondate",
      collectiontime: "collectiontime",
      usercategory: "category",
      prospectemail: "prospectemail",
      prospectmobilenumber: "prospectmobilenumber",
      vehicleId: "v",
    };
    for (const [key, urlKey] of Object.entries(stringMap)) {
      if (q.has(urlKey)) out[key] = q.get(urlKey) || "";
    }
    if (q.has("runners")) out.runners = parseInt(q.get("runners"), 10) || 0;
    if (q.has("floors")) out.floors = parseInt(q.get("floors"), 10) || 0;

    const collection = {};
    if (q.has("collectionlat")) collection.lat = q.get("collectionlat") || "";
    if (q.has("collectionlng")) collection.lng = q.get("collectionlng") || "";
    if (Object.keys(collection).length) out.collection = collection;

    const delivery = {};
    if (q.has("deliverylat")) delivery.lat = q.get("deliverylat") || "";
    if (q.has("deliverylng")) delivery.lng = q.get("deliverylng") || "";
    if (Object.keys(delivery).length) out.delivery = delivery;
  } catch (error) {
    Bugsnag.notify(error);
  }
  return out;
}

const DEFAULT_PARAMS = {
  promocode: "",
  truckType: "",
  description: "",
  collectiondate: "",
  collectiontime: "",
  usercategory: "handheld-items",
  runners: 1,
  floors: 0,
  prospectemail: "",
  prospectmobilenumber: "",
  vehicleId: "",
  collection: { lat: "", lng: "", address: "" },
  delivery: { lat: "", lng: "", address: "" },
  showMore: false,
  agreeToTerms: false,
  extrasCart: {},
};

function applyWidgetConfigOverrides(wc) {
  if (!wc) return {};
  const out = {};
  if (wc.description) {
    out.description = wc.description;
  }
  if (wc.usercategory) out.usercategory = wc.usercategory;
  if (wc.promocode) out.promocode = wc.promocode;
  if (wc.truckType) out.truckType = wc.truckType;
  if (wc.runners != null) out.runners = wc.runners;
  if (wc.floors != null) out.floors = wc.floors;
  if (wc.collection) {
    out.collection = {
      lat: "",
      lng: "",
      address: "",
      ...wc.collection,
    };
  }
  if (wc.delivery) {
    out.delivery = {
      lat: "",
      lng: "",
      address: "",
      ...wc.delivery,
    };
  }
  out.showMore = false;
  return out;
}

function readParams(widgetOverrides = null, storageKey = LS_KEY) {
  const saved = loadSaved(storageKey);
  const url = readUrlParams();
  const widget = applyWidgetConfigOverrides(widgetOverrides);
  const merged = { ...DEFAULT_PARAMS, ...saved, ...url, ...widget };
  // Make sure nested objects are well-formed after merge.
  merged.collection = {
    lat: "",
    lng: "",
    address: "",
    ...(saved.collection || {}),
    ...(url.collection || {}),
  };
  merged.delivery = {
    lat: "",
    lng: "",
    address: "",
    ...(saved.delivery || {}),
    ...(url.delivery || {}),
  };
  return merged;
}

const fire = (fn) => {
  try {
    fn();
  } catch (_) {
    /* analytics is best-effort */
  }
};

const QuoteWidget = ({
  variant = "default",
  widgetConfig = null,
  extrasStoreEnabled,
}) => {
  const isWidgetVariant = variant === "widget";
  const showExtrasStore = resolveQuoteExtrasStoreEnabled({
    widget: isWidgetVariant || isWidgetEmbed(),
    explicit: extrasStoreEnabled,
  });
  const storageKey = isWidgetVariant ? WIDGET_LS_KEY : LS_KEY;
  const pagePath =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const params = useRef(
    readParams(isWidgetVariant ? widgetConfig : null, storageKey),
  ).current;
  const descAutoOpenedRef = useRef(false);

  // Collapse the body-type + vehicle list once the customer has picked a
  // vehicle - the detailed VehicleDetail card takes over below.
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(
    () => !params.truckType,
  );
  // Bumped on "Start over" to remount inputs that keep internal UI state
  // (Google Places autocomplete, vehicle picker body filter, AI panel, etc.).
  const [formResetKey, setFormResetKey] = useState(0);
  const [sessionId, setSessionId] = useState(() => getOrCreateQuoteSessionId());
  const aiTrackingRef = useRef({ trackingId: null, recommendedVehicle: null });

  const [s, setS] = useState({
    vehicletypes: [],
    truckType: params.truckType,
    collection: params.collection,
    delivery: params.delivery,
    usercategory: params.usercategory,
    promocode: params.promocode,
    runners: params.runners,
    floors: params.floors,
    description: params.description,
    collectiondate: params.collectiondate,
    collectiontime: params.collectiontime,
    prospectemail: params.prospectemail,
    prospectmobilenumber: params.prospectmobilenumber,
    mobilenumbererror: false,

    fullQuote: { ...EMPTY_QUOTE },
    loading: false,
    apierror: "",
    acceptedOffer: false,
    showMore: params.showMore,
    addressError: false,
    aiTrigger: 0,
    // Snapshot of the description text that drove the current visible quote.
    // Used to detect when the customer has typed new details and needs to
    // press "Update quote" to refresh the price.
    lastQuotedDescription: "",

    // booking
    firstname: "",
    lastname: "",
    email: params.prospectemail,
    mobilenumber: params.prospectmobilenumber,
    agreeToTerms: params.agreeToTerms,

    // success
    success: false,
    openSuccessModal: false,
    orderid: "",

    extrasCart: params.extrasCart || {},
    extrasCatalog: null,
    extrasSheetOpen: false,
  });

  const sRef = useRef(s);
  useEffect(() => {
    sRef.current = s;
  });

  // ---- persist non-PII form state to localStorage (debounced) ----------
  // Saves on every change after a quiet 400ms - keeps writes well under
  // localStorage's per-frame cost and avoids hammering on every keystroke.
  const persistTimerRef = useRef(null);
  const extrasCatalogLoadedRef = useRef(false);

  useEffect(() => {
    clearTimeout(persistTimerRef.current);
    // Use sRef so we always snapshot the latest state, while the deps array
    // only re-arms the timer when persistable fields actually change.
    persistTimerRef.current = setTimeout(
      () => saveSnapshot(sRef.current, storageKey),
      400,
    );
    return () => clearTimeout(persistTimerRef.current);
  }, [
    s.truckType,
    s.collection,
    s.delivery,
    s.usercategory,
    s.promocode,
    s.runners,
    s.floors,
    s.description,
    s.collectiondate,
    s.collectiontime,
    s.showMore,
    s.agreeToTerms,
    s.extrasCart,
    storageKey,
  ]);

  // Once the order is successfully placed, wipe saved state so the next
  // customer (or the same customer starting a fresh quote) gets a clean slate.
  useEffect(() => {
    if (s.success && s.orderid) clearSaved(storageKey);
  }, [s.success, s.orderid, storageKey]);

  // Desktop: portal <VehicleDetail/> into the dedicated slot in HeroV2's
  // left column so it fills the empty space below the app-store badges
  // instead of stretching the right-column form. Mobile: render inline.
  // We watch matchMedia + look the slot up after mount so the portal target
  // is always live regardless of mount order.
  const [vdetailSlot, setVdetailSlot] = useState(null);
  const [pricePinSlot, setPricePinSlot] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 992px)");
    const update = () => {
      const desktop = mq.matches;
      setVdetailSlot(
        desktop ? document.getElementById("lp2-vdetail-slot") : null,
      );
      setPricePinSlot(
        desktop ? document.getElementById("lp2-price-pin-slot") : null,
      );
    };
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Composite trigger key for the AI suggestions. We encode the user-controlled
  // weather inputs (collectiondate + collectiontime) directly into the key so
  // ANY change to them - once aiTrigger > 0 - causes <AISuggestions/> to
  // re-fetch (and pick up a fresh weather snapshot). aiTrigger itself is still
  // bumped from requestQuote() / vehicle picks / address changes.
  // 0 (number) before the first quote -> child stays gated; non-empty string
  // afterwards -> child fetches and re-fetches on any composite change. The
  // child still enforces a 350ms coalesce + 3000ms hard floor so this can
  // never spam the LLM.
  const aiRefreshKey = s.aiTrigger
    ? `t${s.aiTrigger}|d${s.collectiondate || ""}|h${s.collectiontime || ""}`
    : 0;

  const set = useCallback(
    (patch) => setS((prev) => ({ ...prev, ...patch })),
    [],
  );

  useEffect(() => {
    if (!showExtrasStore) return;
    if (extrasCatalogLoadedRef.current) return;
    extrasCatalogLoadedRef.current = true;
    axios
      .get(baseURL + "/marketplace/quote-extras")
      .then((res) => {
        if (res.data?.status === "success") {
          set({ extrasCatalog: res.data });
        }
      })
      .catch(() => {
        extrasCatalogLoadedRef.current = false;
      });
  }, [set, showExtrasStore]);

  // ---- reset everything --------------------------------------------------
  // Wipes the form, the priced quote, the AI panel state and the persisted
  // localStorage snapshot, so the user can start a fresh trip from scratch.
  // Keeps the loaded `vehicletypes` list in memory (avoids a redundant API
  // hit). Confirms first so an accidental click doesn't destroy work.
  const resetForm = useCallback(() => {
    if (typeof window !== "undefined") {
      const ok = window.confirm(
        "Clear all your details and start a fresh quote?",
      );
      if (!ok) return;
    }
    // Cancel any pending persist write so we don't re-save stale state.
    clearTimeout(persistTimerRef.current);
    clearSaved(storageKey);
    stripQuoteUrlParams();
    setSessionId(getOrCreateQuoteSessionId(true));
    aiTrackingRef.current = { trackingId: null, recommendedVehicle: null };
    descAutoOpenedRef.current = false;
    setVehiclePickerOpen(true);
    setFormResetKey((k) => k + 1);
    setS((prev) => ({
      vehicletypes: prev.vehicletypes,
      truckType: "",
      collection: { lat: "", lng: "", address: "" },
      delivery: { lat: "", lng: "", address: "" },
      usercategory: "handheld-items",
      promocode: "",
      runners: 1,
      floors: 0,
      description: "",
      collectiondate: "",
      collectiontime: "",
      prospectemail: "",
      prospectmobilenumber: "",
      mobilenumbererror: false,

      fullQuote: { ...EMPTY_QUOTE },
      loading: false,
      apierror: "",
      acceptedOffer: false,
      showMore: false,
      addressError: false,
      aiTrigger: 0,
      lastQuotedDescription: "",

      firstname: "",
      lastname: "",
      email: "",
      mobilenumber: "",
      agreeToTerms: false,

      success: false,
      openSuccessModal: false,
      orderid: "",

      extrasCart: {},
      extrasCatalog: null,
      extrasSheetOpen: false,
    }));
    extrasCatalogLoadedRef.current = false;
    fire(() => window.gtag("event", "quote_form_reset"));
    setTimeout(() => {
      document
        .getElementById("quotestart")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, [storageKey]);

  // ---- validation -------------------------------------------------------
  const validStepOne = useCallback((cur) => {
    const {
      truckType,
      delivery,
      collection,
      prospectemail,
      prospectmobilenumber,
      description,
    } = cur;
    if (!prospectemail || prospectemail.length === 0) return false;
    if (!prospectmobilenumber || prospectmobilenumber.length === 0)
      return false;
    if (!truckType) return false;
    if (!description || description.trim().length === 0) return false;
    if (!delivery.lat || delivery.lat === 0) return false;
    if (!collection.lat || collection.lat === 0) return false;
    return !(
      collection.lat === delivery.lat && collection.lng === delivery.lng
    );
  }, []);

  const validStepTwo = useCallback((cur) => {
    const {
      firstname,
      lastname,
      email,
      mobilenumber,
      collectiondate,
      collectiontime,
      description,
    } = cur;
    return !(
      !firstname ||
      !lastname ||
      !email ||
      !mobilenumber ||
      !collectiondate ||
      !collectiontime ||
      !description
    );
  }, []);

  // ---- data fetching ----------------------------------------------------
  useEffect(() => {
    let attempts = 0;
    const fetchVehicleTypes = () => {
      attempts += 1;
      axios
        .get(baseURL + "/vehicle-types")
        .then((res) => set({ vehicletypes: res.data?.vehicles || [] }))
        .catch((e) => {
          Bugsnag.notify(e);
          if (attempts < 3) {
            setTimeout(fetchVehicleTypes, attempts * 1500);
          }
        });
    };
    fetchVehicleTypes();

    if (params.vehicleId && params.vehicleId.length > 3) {
      axios
        .post(baseURL + "/vehicle-profile", {
          vehicleid: params.vehicleId,
          source_:
            util.navigator_() + util.source_() + " qtref:" + document?.referrer,
          ...util.getAllGoogleClickIds(),
        })
        .then((res) => {
          const vp = res.data;
          if (vp?.trucktype) set({ truckType: vp.trucktype });
        })
        .catch((e) => Bugsnag.notify(e));
    }
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- quote ------------------------------------------------------------
  const scrollToQuoteResult = useCallback(() => {
    setTimeout(() => {
      document
        .getElementById("lp2-quoteresult")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const requestQuote = useCallback(
    (overrides = {}) => {
      const cur = { ...sRef.current, ...overrides };
      if (!validStepOne(cur)) return Promise.resolve(false);

      fire(() =>
        window.gtag("event", "quote_requested", { truck_type: cur.truckType }),
      );
      fire(() => window.hj("event", "quote_requested"));

      set({
        loading: true,
        apierror: "",
        // Keep the previous quote visible while refreshing - clearing
        // fullQuote made the entire result block unmount and caused layout
        // jump. Stale price is shown dimmed until the new response lands.
        aiTrigger: (sRef.current.aiTrigger || 0) + 1,
      });

      const quoteStartedAt = Date.now();

      return axios
        .post(baseURL + "/quote", {
          description: cur.description,
          duration: 0,
          trips: 0,
          truckType: cur.truckType,
          delivery: cur.delivery,
          collection: cur.collection,
          category: cur.usercategory,
          promocode: cur.promocode,
          runners: cur.runners,
          floors: cur.floors,
          prospectemail: cur.prospectemail,
          collectiondate: cur.collectiondate,
          collectiontime: cur.collectiontime,
          prospectmobilenumber: cur.prospectmobilenumber,
          source_:
            util.navigator_() + util.source_() + " qtref:" + document?.referrer,
          ...util.getAllGoogleClickIds(),
          extras: showExtrasStore ? buildExtrasPayload(cur.extrasCart) : [],
        })
        .then((response) => {
          if (response.data.sellingPrice > 0) {
            fire(() =>
              window.gtag("event", "quote_received", {
                truck_type: cur.truckType,
                value: response.data.sellingPrice / 100,
                currency: "ZAR",
              }),
            );
            fire(() => window.hj("event", "quote_received"));
            util.reportGoogleAdsQuoteAddToCart({
              value: response.data.sellingPrice / 100,
              currency: "ZAR",
            });
            set({
              loading: false,
              fullQuote: {
                tripDistance: response.data.tripDistance,
                tripDuration: response.data.tripDuration,
                sellingPrice: response.data.sellingPrice,
                storedQuoteId: response.data.quoteId,
                discount: response.data.discount,
                otherOptions: response.data.otherOptions || [],
                quotedRunners: cur.runners ?? 0,
                quotedFloors: cur.floors ?? 0,
                quotedPromocode: cur.promocode || "",
                quotedCollectiontime: cur.collectiontime || "",
                quoteLatencyMs: Date.now() - quoteStartedAt,
                extrasLines: response.data.extrasLines || [],
                extrasTotalCents: response.data.extrasTotalCents || 0,
                extrasSubtotalCents: response.data.extrasSubtotalCents || 0,
                extrasVatCents: response.data.extrasVatCents || 0,
              },
              lastQuotedDescription: cur.description || "",
            });
            scrollToQuoteResult();
            return true;
          }
          set({ loading: false });
          return false;
        })
        .catch((e) => {
          set({
            loading: false,
            apierror: "Check your addresses or vehicle type and try again.",
          });
          Bugsnag.notify(e);
          return false;
        });
    },
    [set, validStepOne, scrollToQuoteResult, showExtrasStore],
  );

  const flushExtrasQuoteIfDirty = useCallback(() => {
    if (!showExtrasStore) return Promise.resolve(true);
    const cur = sRef.current;
    if (
      cur.fullQuote.sellingPrice <= 0 ||
      !validStepOne(cur) ||
      !extrasCartIsDirty(cur.extrasCart, cur.fullQuote.extrasLines)
    ) {
      return Promise.resolve(true);
    }
    return requestQuote();
  }, [requestQuote, validStepOne, showExtrasStore]);

  // ---- accept / book ----------------------------------------------------
  const acceptQuote = useCallback(() => {
    flushExtrasQuoteIfDirty().then((ok) => {
      if (!ok) return;

      const cur = sRef.current;
    const ai = aiTrackingRef.current;
    trackAiSuggestionEvent({
      eventType: "book_vehicle",
      trackingId: ai.trackingId,
      quoteId: cur.fullQuote.storedQuoteId || undefined,
      sessionId,
      source_: buildQuoteSource_(),
      eventData: {
        truckType: cur.truckType,
        recommendedVehicle: ai.recommendedVehicle,
        matchedRecommendation: ai.recommendedVehicle === cur.truckType,
        sellingPriceCents: cur.fullQuote.sellingPrice,
      },
    });
    fire(() =>
      window.gtag("event", "booking_submitted", {
        truck_type: cur.truckType,
        value: cur.fullQuote.sellingPrice / 100,
        currency: "ZAR",
      }),
    );
    fire(() => window.hj("event", "booking_submitted"));

    set({ loading: true, apierror: "" });

    axios
      .post(baseURL + "/accept-quote", {
        fullQuote: cur.fullQuote,
        firstname: cur.firstname,
        lastname: cur.lastname,
        email: cur.email,
        mobilenumber: cur.mobilenumber,
        collectiondate: cur.collectiondate,
        collectiontime: cur.collectiontime,
        description: cur.description,
        acceptedOffer: "sellingprice",
        aiSuggestionLogId: ai.trackingId || undefined,
        extras: showExtrasStore ? buildExtrasPayload(cur.extrasCart) : [],
      })
      .then((response) => {
        const ok = response.status === 200;
        const newOrderId = response?.data?.orderid;

        const finishSuccess = () => {
          set({
            loading: false,
            success: ok,
            openSuccessModal: ok,
            orderid: newOrderId || "",
          });
          if (ok && (isWidgetEmbed() || isWidgetVariant)) {
            notifyWidgetParent("qiktruck:booked", {
              orderid: newOrderId || "",
              site: getWidgetSiteId() || widgetConfig?.site || "",
            });
          }
        };

        if (ok && newOrderId?.length > 0) {
          trackAiSuggestionEvent({
            eventType: "order_confirmed",
            trackingId: ai.trackingId,
            quoteId: cur.fullQuote.storedQuoteId || undefined,
            sessionId,
            source_: buildQuoteSource_(),
            eventData: {
              orderid: newOrderId,
              truckType: cur.truckType,
              recommendedVehicle: ai.recommendedVehicle,
              matchedRecommendation: ai.recommendedVehicle === cur.truckType,
            },
          });
          fire(() =>
            window.gtag("event", "booking_confirmed", {
              truck_type: cur.truckType,
              value: cur.fullQuote.sellingPrice / 100,
              currency: "ZAR",
              order_id: newOrderId,
            }),
          );
          fire(() =>
            window.fbq("track", "Purchase", {
              value: cur.fullQuote.sellingPrice / 100,
              currency: "ZAR",
            }),
          );
          fire(() => window.hj("event", "booking_confirmed"));

          cookie.save(
            "currentOrder",
            `email=${cur.email}&orderid=${newOrderId}`,
            {
              path: "/",
              maxAge: 86400,
              secure: true,
            },
          );

          axios
            .post(baseURL + "/track-order", {
              orderid: newOrderId,
              email: cur.email,
            })
            .then((trackRes) => {
              if (trackRes.data?.orderid && trackRes.data?.token) {
                persistOrderTrackSession(trackRes.data, {
                  email: cur.email,
                  orderid: newOrderId,
                });
              }
            })
            .catch(() => {
              /* chat unlocks when customer opens track-order */
            })
            .finally(finishSuccess);
          return;
        }

        finishSuccess();
      })
      .catch(() =>
        set({ loading: false, apierror: "Request failed. Please try again." }),
      );
    });
  }, [flushExtrasQuoteIfDirty, set, sessionId, isWidgetVariant, widgetConfig?.site, showExtrasStore]);

  // ---- handlers ---------------------------------------------------------
  const handleSelectVehicle = useCallback(
    (type, opts = {}) => {
      set({ truckType: type });
      requestQuote({ truckType: type });

      // Only advance the page when the customer actively picks a vehicle from
      // the list (not when a body type auto-selects a default vehicle, and not
      // when swapping vehicles from the price/other-options view).
      if (!opts.advance) return;

      setVehiclePickerOpen(false);

      // Scroll to the detailed vehicle summary so they can confirm their pick.
      // No input focus - avoids fighting mobile keyboards / quote-result scroll.
      // If a priced quote comes back, requestQuote's scroll-to-result takes over.
      setTimeout(() => {
        document
          .getElementById("lp2-vehicledetail")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 90);
    },
    [set, requestQuote],
  );

  const handleAiTrackingUpdate = useCallback((meta) => {
    aiTrackingRef.current = {
      trackingId: meta?.trackingId ?? null,
      recommendedVehicle: meta?.recommendedVehicle ?? null,
    };
  }, []);

  const handleApplyAiRecommendation = useCallback(
    (type) => handleSelectVehicle(type, { advance: true }),
    [handleSelectVehicle],
  );

  const handleAiDescriptionDetail = useCallback(
    (question, answer) => {
      try {
        const q = normaliseDashes((question || "").trim());
        const a = normaliseDashes((answer || "").trim());
        if (!a) return;
        const current = sRef.current.description || "";
        const marker = `- ${q}`;
        const newLine = `${marker} ${a}`;
        const lines = current ? current.split("\n") : [];
        const idx = lines.findIndex((line) =>
          line.trimStart().startsWith(marker),
        );
        if (idx >= 0) {
          if (lines[idx] === newLine) return;
          lines[idx] = newLine;
        } else {
          lines.push(newLine);
        }
        const next = lines.join("\n").replace(/^\n+/, "");
        if (next === current) return;
        set({ description: next });
      } catch (err) {
        Bugsnag.notify(err);
        const append = `- ${(question || "").trim()} ${(answer || "").trim()}`.trim();
        if (!append.trim() || append === "-") return;
        const current = sRef.current.description || "";
        set({
          description: current ? `${current}\n${append}` : append,
        });
      }
    },
    [set],
  );

  const quoteSource_ = isWidgetVariant && widgetConfig?.source_
    ? widgetConfig.source_ + " " + buildQuoteSource_()
    : buildQuoteSource_();

  const optionsRequoteTimerRef = useRef(null);
  const extrasRequoteTimerRef = useRef(null);
  const scheduleOptionsRequote = useCallback(() => {
    clearTimeout(optionsRequoteTimerRef.current);
    optionsRequoteTimerRef.current = setTimeout(() => {
      if (sRef.current.fullQuote.sellingPrice > 0 && validStepOne(sRef.current)) {
        requestQuote();
      }
    }, 400);
  }, [requestQuote, validStepOne]);

  const handleExtrasQtyChange = useCallback(
    (productId, qty) => {
      if (!showExtrasStore) return;
      setS((prev) => {
        const cart = { ...(prev.extrasCart || {}) };
        const n = Math.max(0, parseInt(qty, 10) || 0);
        if (n <= 0) delete cart[productId];
        else cart[productId] = n;
        return { ...prev, extrasCart: cart };
      });
      // In-sheet edits are preview-only until Done. Featured-row +/- (sheet
      // closed) debounces a single server quote so we don't flood quotations.
      if (!sRef.current.extrasSheetOpen) {
        clearTimeout(extrasRequoteTimerRef.current);
        extrasRequoteTimerRef.current = setTimeout(() => {
          flushExtrasQuoteIfDirty();
        }, 600);
      }
    },
    [flushExtrasQuoteIfDirty, showExtrasStore],
  );

  const openExtrasSheet = useCallback(() => {
    set({ extrasSheetOpen: true });
  }, [set]);

  const closeExtrasSheet = useCallback(() => {
    set({ extrasSheetOpen: false });
    flushExtrasQuoteIfDirty();
  }, [set, flushExtrasQuoteIfDirty]);

  const scrollToExtras = useCallback(() => {
    openExtrasSheet();
    setTimeout(() => {
      document
        .getElementById("lp2-trip-extras")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }, [openExtrasSheet]);

  const scrollToDescription = useCallback(() => {
    document
      .getElementById("lp2-desc")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const el = document.getElementById("lp2-desc");
      el?.focus?.();
    }, 320);
  }, []);

  const handleCollection = useCallback(
    (loc) => {
      const normalised = {
        lat: loc?.lat ?? "",
        lng: loc?.lng ?? "",
        address: loc?.address ?? "",
      };
      set({ collection: normalised, addressError: false });
      requestQuote({ collection: normalised });
    },
    [set, requestQuote],
  );

  const handleDelivery = useCallback(
    (loc) => {
      const normalised = {
        lat: loc?.lat ?? "",
        lng: loc?.lng ?? "",
        address: loc?.address ?? "",
      };
      set({ delivery: normalised, addressError: false });
      requestQuote({ delivery: normalised });
    },
    [set, requestQuote],
  );

  // Called when the customer tries to choose a vehicle/body type before
  // entering both addresses - nudge them back up to the address fields.
  const handleNeedAddresses = useCallback(() => {
    set({ addressError: true });
    setTimeout(() => {
      document
        .getElementById("lp2-addresses")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 30);
  }, [set]);

  const acceptOffer = useCallback(() => {
    flushExtrasQuoteIfDirty().then((ok) => {
      if (!ok) return;

      const cur = sRef.current;
      const ai = aiTrackingRef.current;
      trackAiSuggestionEvent({
        eventType: "accept_quote",
        trackingId: ai.trackingId,
        quoteId: cur.fullQuote.storedQuoteId || undefined,
        sessionId,
        source_: buildQuoteSource_(),
        eventData: {
          truckType: cur.truckType,
          recommendedVehicle: ai.recommendedVehicle,
          matchedRecommendation: ai.recommendedVehicle === cur.truckType,
          sellingPriceCents: cur.fullQuote.sellingPrice,
        },
      });
      fire(() =>
        window.gtag("event", "quote_accepted", {
          truck_type: cur.truckType,
          value: cur.fullQuote.sellingPrice / 100,
          currency: "ZAR",
        }),
      );
      fire(() => window.hj("event", "quote_accepted"));
      set({ acceptedOffer: true });
      setTimeout(() => {
        document
          .getElementById("lp2-makebooking")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    });
  }, [flushExtrasQuoteIfDirty, set, sessionId]);

  const scrollToBooking = useCallback(() => {
    document
      .getElementById("lp2-makebooking")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const selectedVehicle = findVehicle(s.vehicletypes, s.truckType);
  const contactFilled =
    !!(s.prospectemail || "").trim() && !!(s.prospectmobilenumber || "").trim();

  // Bounce hint: pre-quote → contact fields; post-accept → booking form.
  const contactNudgeEnabled =
    !!selectedVehicle &&
    !vdetailSlot &&
    !vehiclePickerOpen &&
    s.fullQuote.sellingPrice <= 0 &&
    !contactFilled;

  const bookingNudgeEnabled =
    !vdetailSlot &&
    s.fullQuote.sellingPrice > 0 &&
    s.acceptedOffer &&
    !s.success;

  const showContactNudge = useScrollNudge("lp2-contact", contactNudgeEnabled);
  const showBookingNudge = useScrollNudge(
    "lp2-makebooking",
    bookingNudgeEnabled,
  );
  const canQuote = validStepOne(s) && !s.loading;
  const canSubmit = validStepTwo(s) && s.agreeToTerms;

  // True when the customer has typed new description details since the last
  // successful priced quote - we use this to nudge them to refresh the price
  // (visually emphasising the "Update quote" button + a hint by the textarea)
  // instead of silently re-firing the API on every keystroke.
  const descriptionStale =
    s.fullQuote.sellingPrice > 0 &&
    (s.description || "").trim() !==
      (s.lastQuotedDescription || "").trim();

  const optionsStale =
    s.fullQuote.sellingPrice > 0 &&
    ((s.runners ?? 0) !== (s.fullQuote.quotedRunners ?? 0) ||
      (s.floors ?? 0) !== (s.fullQuote.quotedFloors ?? 0) ||
      (s.promocode || "") !== (s.fullQuote.quotedPromocode || ""));
  const addressesComplete = !!(s.collection.lat && s.delivery.lat);

  // True when the customer has done everything else for a quote but their
  // email and/or mobile are still missing - we then highlight the specific
  // empty input(s) so they immediately see what's blocking the button.
  // Mirrors the priority of quoteHint below so the messaging and the
  // visual highlight stay in lockstep.
  const emailEmpty = !s.prospectemail || !s.prospectemail.trim();
  const mobileEmpty =
    !s.prospectmobilenumber || !s.prospectmobilenumber.trim();
  const blockedByContact =
    !s.loading &&
    addressesComplete &&
    !!(s.description && s.description.trim()) &&
    !!s.truckType &&
    (emailEmpty || mobileEmpty);
  const emailNeeded = blockedByContact && emailEmpty;
  const mobileNeeded = blockedByContact && mobileEmpty;

  // Show the "Start over" affordance only when the user actually has
  // something worth clearing - keeps the UI uncluttered on a fresh visit.
  const hasUserInput =
    !!s.truckType ||
    !!(s.description && s.description.trim()) ||
    !!s.collection.lat ||
    !!s.delivery.lat ||
    !!s.promocode ||
    !!s.collectiondate ||
    !!s.collectiontime ||
    !!s.prospectemail ||
    !!s.prospectmobilenumber ||
    (s.runners ?? 1) !== 1 ||
    (s.floors ?? 0) !== 0 ||
    s.fullQuote.sellingPrice > 0;

  // Tell the customer exactly what's still needed for the quote button.
  let quoteHint = "";
  if (!canQuote && !s.loading) {
    if (!addressesComplete) {
      quoteHint =
        isWidgetVariant && widgetConfig?.lockPickup
          ? "Enter your delivery address to start."
          : "Enter your pick-up and drop-off to start.";
    } else if (!s.description || !s.description.trim())
      quoteHint = "Tell us what you're moving.";
    else if (!s.truckType) quoteHint = "Choose a body type and vehicle below.";
    else if (!s.prospectemail || !s.prospectmobilenumber)
      quoteHint = "Add your email and mobile number.";
  }

  return (
    <div
      className={
        "lp2-quote-card" + (isWidgetVariant ? " lp2-quote-card--widget" : "")
      }
      id="quotestart"
    >
      {/* Fixed top progress bar - visible anywhere on the page during quote fetch */}
      {s.loading && <div className="lp2-progress-bar" aria-hidden="true" />}

      {!isWidgetVariant && (
      <div className="lp2-quote-head">
        <h2>
          Quote for your{" "}
          <span className="orange">truck or bakkie</span>with a
          driver
        </h2>
        <p>Enter your trip, compare vehicles, and get an instant price.</p>
        <p className="orange">This is a free online quote. You will get a response in seconds.</p>
        {hasUserInput && (
          <button
            type="button"
            className="lp2-quote-reset"
            onClick={resetForm}
            title="Clear all fields and start a fresh quote"
            aria-label="Clear all fields and start over"
          >
            <i className="mdi mdi-refresh" aria-hidden="true" />
            Start over
          </button>
        )}
      </div>
      )}

      <div className="lp2-quote-body" key={formResetKey}>
        {/* Addresses */}
        <div className="lp2-field-group" id="lp2-addresses">
          <AddressFields
            highlight={s.addressError}
            initialCollectionAddress={s.collection?.address || ""}
            initialDeliveryAddress={s.delivery?.address || ""}
            onCollection={handleCollection}
            onDelivery={handleDelivery}
            hideCollection={isWidgetVariant && widgetConfig?.lockPickup}
            deliveryOnlyLabel={
              isWidgetVariant && widgetConfig?.lockPickup
                ? "Where should we deliver?"
                : undefined
            }
          />
          {s.addressError && (
            <p className="lp2-error" role="alert">
              <i className="mdi mdi-information-outline" aria-hidden="true" />{" "}
              Please enter your pick-up and drop-off addresses first.
            </p>
          )}
        </div>

        {/* What are we moving (required) - before vehicle so load drives body type */}
        <div className="lp2-field-group" id="lp2-desc-section">
          <label className="lp2-label" htmlFor="lp2-desc">
            What are we moving?<span className="lp2-req">*</span>
          </label>
          <AutoResizeTextarea
            id="lp2-desc"
            className="lp2-input lp2-input--area"
            value={s.description}
            placeholder="Tell us about everything you are moving - names, sizes and quantity."
            onChange={(e) => {
              const val = e.target.value;
              const patch = { description: val };
              // Once they start describing the load, reveal the extra options
              // (helpers, floors, promo, schedule) so nothing is missed.
              if (val.trim().length > 0 && !descAutoOpenedRef.current) {
                patch.showMore = true;
                descAutoOpenedRef.current = true;
              }
              set(patch);
            }}
          />
          {descriptionStale && (
            <p className="lp2-desc-stale" aria-live="polite">
              <i className="mdi mdi-refresh" aria-hidden="true" />
              Description changed - click <strong>Update quote</strong> to
              refresh your price.
            </p>
          )}
        </div>

        {/* Vehicle */}
        <div className="lp2-field-group" id="lp2-vehicle-picker">
          <VehiclePicker
            vehicletypes={s.vehicletypes}
            selected={s.truckType}
            open={vehiclePickerOpen}
            onOpenChange={setVehiclePickerOpen}
            onSelect={handleSelectVehicle}
            addressesComplete={addressesComplete}
            onNeedAddresses={handleNeedAddresses}
          />
        </div>

        {/* Selected vehicle - detailed view. Inline in the form on mobile;
            portaled to the empty hero left-column slot on desktop. */}
        {selectedVehicle &&
          (() => {
            const card = (
              <VehicleDetail
                vehicle={selectedVehicle}
                vehicletypes={s.vehicletypes}
                onSelectSibling={(type) =>
                  handleSelectVehicle(type, { advance: false })
                }
                onChange={() => {
                  setVehiclePickerOpen(true);
                  setTimeout(() => {
                    document
                      .getElementById("lp2-vehicle-picker")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 90);
                }}
              />
            );
            return vdetailSlot ? createPortal(card, vdetailSlot) : card;
          })()}

        {/* Contact */}
        <div className="lp2-field-group lp2-grid-2" id="lp2-contact">
          <div>
            <label
              className={"lp2-label" + (emailNeeded ? " is-needed" : "")}
              htmlFor="lp2-email"
            >
              Email<span className="lp2-req">*</span>
            </label>
            <input
              id="lp2-email"
              className={"lp2-input" + (emailNeeded ? " is-needed" : "")}
              type="email"
              placeholder="you@example.com"
              value={s.prospectemail}
              onChange={(e) =>
                set({ prospectemail: e.target.value, email: e.target.value })
              }
              aria-invalid={emailNeeded || undefined}
            />
          </div>
          <div>
            <label
              className={"lp2-label" + (mobileNeeded ? " is-needed" : "")}
              htmlFor="lp2-mobile"
            >
              Mobile number<span className="lp2-req">*</span>
            </label>
            <input
              id="lp2-mobile"
              className={"lp2-input" + (mobileNeeded ? " is-needed" : "")}
              type="tel"
              placeholder="0821234567"
              value={s.prospectmobilenumber}
              onChange={(e) => {
                const v = e.target.value;
                set({
                  prospectmobilenumber: v,
                  mobilenumber: v,
                  mobilenumbererror: v.length < 10 || /^\d+$/.exec(v) === null,
                });
              }}
              aria-invalid={mobileNeeded || undefined}
            />
            {s.mobilenumbererror && (
              <p
                className="lp2-help"
                style={{ color: "var(--lp2-orange-dark)" }}
              >
                Enter a valid mobile number
              </p>
            )}
          </div>
        </div>

        {/* More details (collapsible) */}
        <div className="lp2-disclosure">
          <button
            type="button"
            className={"lp2-disclosure-toggle" + (s.showMore ? " is-open" : "")}
            aria-expanded={s.showMore}
            onClick={() => set({ showMore: !s.showMore })}
          >
            <span>
              <i className="mdi mdi-tune" aria-hidden="true" /> Helpers, floors,
              promo code &amp; schedule
            </span>
            <i className="mdi mdi-chevron-down" aria-hidden="true" />
          </button>

          {s.showMore && (
            <div className="lp2-disclosure-content">
              <div className="lp2-field-group">
                <div className="lp2-opt-row">
                  <i
                    className="mdi mdi-stairs lp2-opt-icon"
                    aria-hidden="true"
                  />
                  <div className="lp2-opt-text">
                    <label className="lp2-opt-label" htmlFor="lp2-floors">
                      Floors
                    </label>
                    <span className="lp2-opt-desc">
                      Flights of stairs we carry up or down when there's no
                      lift.
                    </span>
                  </div>
                  <input
                    id="lp2-floors"
                    className="lp2-input lp2-opt-num"
                    type="number"
                    min={0}
                    value={s.floors}
                    onChange={(e) => {
                      set({ floors: parseInt(e.target.value, 10) || 0 });
                      scheduleOptionsRequote();
                    }}
                  />
                </div>

                <div className="lp2-opt-row">
                  <i
                    className="mdi mdi-account-multiple lp2-opt-icon"
                    aria-hidden="true"
                  />
                  <div className="lp2-opt-text">
                    <label className="lp2-opt-label" htmlFor="lp2-helpers">
                      Helpers
                    </label>
                    <span className="lp2-opt-desc">
                      Extra people to help load and carry. Your driver may not
                      be able to assist.
                    </span>
                  </div>
                  <input
                    id="lp2-helpers"
                    className="lp2-input lp2-opt-num"
                    type="number"
                    min={0}
                    value={s.runners}
                    onChange={(e) => {
                      set({ runners: parseInt(e.target.value, 10) || 0 });
                      scheduleOptionsRequote();
                    }}
                  />
                </div>

                <div className="lp2-opt-row">
                  <i
                    className="mdi mdi-tag lp2-opt-icon"
                    aria-hidden="true"
                  />
                  <div className="lp2-opt-text">
                    <label className="lp2-opt-label" htmlFor="lp2-promo">
                      Promo code
                    </label>
                    <span className="lp2-opt-desc">
                      Got a discount code? Apply it for up to 30% off.
                    </span>
                  </div>
                  <input
                    id="lp2-promo"
                    className="lp2-input lp2-opt-code"
                    type="text"
                    value={s.promocode}
                    placeholder="CODE"
                    onChange={(e) =>
                      set({ promocode: e.target.value.toUpperCase() })
                    }
                    onBlur={scheduleOptionsRequote}
                  />
                </div>
              </div>

              {optionsStale && (
                <p className="lp2-desc-stale" aria-live="polite">
                  <i className="mdi mdi-refresh" aria-hidden="true" />
                  Options changed - updating your price…
                </p>
              )}

              <div className="lp2-grid-2 lp2-dt-grid lp2-field-group">
                <div>
                  <label className="lp2-label" htmlFor="lp2-date">
                    Collection date
                  </label>
                  <input
                    id="lp2-date"
                    className="lp2-input"
                    type="date"
                    value={s.collectiondate}
                    onChange={(e) => set({ collectiondate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="lp2-label" htmlFor="lp2-time">
                    Collection time
                  </label>
                  <input
                    id="lp2-time"
                    className="lp2-input"
                    type="time"
                    value={s.collectiontime}
                    onChange={(e) => set({ collectiontime: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Primary CTA - becomes a secondary "update" action once a price is shown
            so it doesn't compete with the "Book this vehicle" button below. */}
        <button
          type="button"
          className={
            "lp2-btn lp2-btn--block lp2-btn--lg " +
            (s.fullQuote.sellingPrice > 0 && !descriptionStale
              ? "lp2-btn--ghost"
              : "lp2-btn--primary") +
            (descriptionStale ? " lp2-btn--attention" : "")
          }
          style={{ marginTop: 18 }}
          onClick={() => requestQuote()}
          disabled={!canQuote}
        >
          {s.loading ? (
            <span className="lp2-spin" aria-hidden="true" />
          ) : s.fullQuote.sellingPrice > 0 ? (
            <>
              <i className="mdi mdi-refresh" aria-hidden="true" />
              Update quote
            </>
          ) : (
            <>
              Get quotations
              <i className="mdi mdi-arrow-down-bold" aria-hidden="true" />
            </>
          )}
        </button>

        {quoteHint && s.fullQuote.sellingPrice <= 0 ? (
          <p className="lp2-quote-hint">
            <i className="mdi mdi-information-outline" aria-hidden="true" />
            {quoteHint}
          </p>
        ) : null}

        {showExtrasStore &&
        s.fullQuote.sellingPrice <= 0 &&
        (s.extrasCatalog?.totalCount || 0) > 0 ? (
          <p className="lp2-extras-prequote-hint">
            <i className="mdi mdi-package-variant" aria-hidden="true" />
            Now you can add moving supplies to your quote. For example, boxes, wrap, tape.
          </p>
        ) : null}

        {!s.loading && s.apierror ? (
          <p className="lp2-error">{s.apierror}</p>
        ) : null}

        {/* First-quote skeleton - reserves space before a price exists so the
            result block doesn't pop in and shove content below. */}
        {s.loading && s.fullQuote.sellingPrice <= 0 && (
          <div className="lp2-result lp2-result--skeleton" aria-hidden="true">
            <div className="lp2-result-top">
              <div className="lp2-skel lp2-skel--truck" />
              <div className="lp2-result-meta">
                <div className="lp2-skel lp2-skel--line lp2-skel--sm" />
                <div className="lp2-skel lp2-skel--line lp2-skel--lg" />
                <div className="lp2-skel lp2-skel--line lp2-skel--md" />
              </div>
            </div>
            <div className="lp2-skel lp2-skel--btn" />
          </div>
        )}

        {/* Result - all-in price → AI suggestions → other vehicle options */}
        <QuoteResult
          fullQuote={s.fullQuote}
          truckType={s.truckType}
          loading={s.loading}
          onAccept={acceptOffer}
          onSelectOther={handleSelectVehicle}
          vehicletypes={s.vehicletypes}
          optionsStale={optionsStale}
          collectiontime={s.collectiontime}
          quoteLatencyMs={s.fullQuote.quoteLatencyMs}
          pricePinSlot={pricePinSlot}
          aiTrigger={s.aiTrigger}
          acceptedOffer={s.acceptedOffer}
          onScrollToBooking={scrollToBooking}
          {...(showExtrasStore
            ? {
                extrasCatalog: s.extrasCatalog,
                extrasCart: s.extrasCart,
                onExtrasQtyChange: handleExtrasQtyChange,
                extrasSheetOpen: s.extrasSheetOpen,
                onOpenExtrasSheet: openExtrasSheet,
                onCloseExtrasSheet: closeExtrasSheet,
              }
            : {})}
        >
          {/* AI overlay - runs in parallel with /quote, never blocks pricing.
              Only fetches when aiTrigger bumps (i.e. on real quote actions),
              never on raw keystrokes, to keep LLM cost predictable. Lives
              between the all-in price card and the alternative options. */}
          {!isWidgetVariant && (
          <AISuggestions
            triggerKey={aiRefreshKey}
            description={s.description}
            truckType={s.truckType}
            helpers={s.runners}
            floors={s.floors}
            trips={1}
            collectiondate={s.collectiondate}
            collectiontime={s.collectiontime}
            collection={s.collection}
            delivery={s.delivery}
            tripDistanceMeters={s.fullQuote.tripDistance}
            tripDurationSeconds={s.fullQuote.tripDuration}
            vehicletypes={s.vehicletypes}
            currentPriceCents={s.fullQuote.sellingPrice}
            otherOptions={s.fullQuote.otherOptions}
            quoteId={s.fullQuote.storedQuoteId}
            sessionId={sessionId}
            source_={quoteSource_}
            pagePath={pagePath}
            onTrackingUpdate={handleAiTrackingUpdate}
            onApplyRecommendation={handleApplyAiRecommendation}
            onAddDescriptionDetail={handleAiDescriptionDetail}
            descriptionStale={descriptionStale}
            quoteLoading={s.loading}
            onUpdateQuote={requestQuote}
            onEditDescription={scrollToDescription}
            onBook={acceptOffer}
            quotedRunners={s.fullQuote.quotedRunners}
            quotedFloors={s.fullQuote.quotedFloors}
            quotedCollectiontime={s.fullQuote.quotedCollectiontime}
          />
          )}
        </QuoteResult>

        {/* Booking */}
        {s.fullQuote.sellingPrice > 0 && s.acceptedOffer && (
          <BookingForm
            values={s}
            set={set}
            price={s.fullQuote.sellingPrice}
            truckType={s.truckType}
            vehicletypes={s.vehicletypes}
            tripDistance={s.fullQuote.tripDistance}
            tripDuration={s.fullQuote.tripDuration}
            quotedRunners={s.fullQuote.quotedRunners}
            quotedFloors={s.fullQuote.quotedFloors}
            extrasLines={showExtrasStore ? s.fullQuote.extrasLines : []}
            extrasTotalCents={showExtrasStore ? s.fullQuote.extrasTotalCents : 0}
            onChangeSupplies={showExtrasStore ? scrollToExtras : undefined}
            loading={s.loading}
            apierror={s.apierror}
            agreeToTerms={s.agreeToTerms}
            onToggleTerms={() => set({ agreeToTerms: !s.agreeToTerms })}
            canSubmit={canSubmit}
            onSubmit={acceptQuote}
          />
        )}
      </div>

      {/* Success modal */}
      {s.success && s.openSuccessModal && (
        <div
          className="lp2-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Order received"
        >
          <div className="lp2-modal">
            <div className="lp2-modal-head">
              <i className="mdi mdi-check-circle" aria-hidden="true" />
              Order received
            </div>
            <div className="lp2-modal-body">
              <ul className="lp2-modal-list">
                <li>
                  <i className="mdi mdi-account-tie" aria-hidden="true" />A
                  driver will be in touch shortly to confirm your order.
                </li>
                <li>
                  <i className="mdi mdi-email-outline" aria-hidden="true" />
                  You will receive an email with your invoice shortly.
                </li>
                <li>
                  <i className="mdi mdi-cash-multiple" aria-hidden="true" />
                  You can pay now or after the delivery.
                </li>
                <li>
                  <i
                    className="mdi mdi-card-account-details-outline"
                    aria-hidden="true"
                  />
                  Produce your ID when the driver arrives.
                </li>
                <li>
                  <i className="mdi mdi-message-text-outline" aria-hidden="true" />
                  Message dispatch in <strong>Trip messages</strong> on your order
                  page - we&apos;ll confirm details there.
                </li>
                <li>
                  <i className="mdi mdi-truck-check" aria-hidden="true" />
                  Happy moving!
                </li>
              </ul>
            </div>
            <div className="lp2-modal-foot lp2-modal-foot--split">
              {isWidgetEmbed() || isWidgetVariant ? (
                <button
                  type="button"
                  className="lp2-btn lp2-btn--primary"
                  onClick={() => {
                    set({ openSuccessModal: false });
                    notifyWidgetParent("qiktruck:close");
                  }}
                >
                  Done
                </button>
              ) : (
                <>
              <button
                type="button"
                className="lp2-btn lp2-btn--primary"
                onClick={() => {
                  set({ openSuccessModal: false });
                  window.location.href =
                    "/track-order?tab=messages&orderid=" +
                    encodeURIComponent(s.orderid) +
                    "&email=" +
                    encodeURIComponent(s.email);
                }}
              >
                Message dispatch
                <i className="mdi mdi-message-text-outline" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="lp2-btn lp2-btn--ghost"
                onClick={() => {
                  set({ openSuccessModal: false });
                  window.location.href =
                    "/track-order?paynow=yes&orderid=" +
                    encodeURIComponent(s.orderid) +
                    "&email=" +
                    encodeURIComponent(s.email);
                }}
              >
                Track &amp; pay
              </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {!isWidgetVariant && (
      <>
      <ScrollNudge
        visible={showContactNudge}
        targetId="lp2-contact"
        ariaLabel="Scroll to email and get your quotation"
      />
      <ScrollNudge
        visible={showBookingNudge}
        targetId="lp2-makebooking"
        ariaLabel="Scroll to complete your booking"
      />
      </>
      )}

      {/* keep selectedVehicle referenced for potential future tonnage display */}
      {selectedVehicle?.tonnes ? (
        <span style={{ display: "none" }}>{selectedVehicle.tonnes}</span>
      ) : null}
    </div>
  );
};

export default QuoteWidget;
